"use client"

import { forwardRef, useState } from "react"
import { isTaskableBusiness, isTaskableContact, isTaskableHousehold, Task, TaskPriority, TaskStatus } from "@/schema"
import { useGlobalSlideoverStore } from "@/stores/use-global-slideover-store"
import CircleCheckbox from "@ui/circle-checkbox"
import { SelectInput } from "@ui/forms/inputs/select-input"
import { MaskedIcon } from "@ui/masked-icon"
import {
  Badge,
  Button,
  Checkbox,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { format, formatDistanceToNow } from "date-fns"
import {
  AtSign,
  Building2,
  CalendarDays,
  FolderOpen,
  Lock,
  MessageSquare,
  Repeat,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  Undo2,
  WandSparkles,
} from "lucide-react"
import { toast } from "sonner"

import { householdRouteSegment } from "@/lib/household-route-segment"
import { useDeactivateTaskRecurrence, useDeleteTask, useUpdateTask } from "@/hooks/api/tasks/mutations"
import { Link } from "@/components/book-link"
import { BookIcon } from "@/components/books/book-icon"
import { ConfirmDialog } from "@/components/dialogs"
import { TaskDetailSlideover, TaskDetailWrapper } from "@/components/tasks/detail-slideover"
import { LexicalPreview } from "@/components/tasks/lexical-preview"
import { PrivacyViewerList } from "@/components/tasks/shared/privacy-viewer-list"
import { AssignedToCell } from "@/components/tasks/table/assigned-to-cell"
import { monthlyWeekdaySummary } from "@/components/tasks/utils/monthly-recurrence"

import { useOptimisticTaskActions } from "../use-optimistic-task-actions"
import { Deadline } from "./deadline"

const TRANSCRIPT_SEEK_OFFSET_SECONDS = 2

const getPriorityIcon = (priority: TaskPriority | null | undefined) => {
  switch (priority) {
    case "low":
      return <MaskedIcon src="/icons/low-priority.svg" className="size-3.5 text-inherit" />
    case "medium":
      return <MaskedIcon src="/icons/medium-priority.svg" className="size-3.5 text-inherit" />
    case "high":
      return <MaskedIcon src="/icons/high-priority.svg" className="size-3.5 text-inherit" />
    case "urgent":
      return (
        <div className="bg-icon hover:bg-primary flex size-3.5 items-center justify-center rounded-sm text-xs font-medium text-white">
          !
        </div>
      )
    default:
      return <div className="text-muted-foreground">--</div>
  }
}

const PRIORITY_OPTIONS: { value: string; label: React.ReactNode }[] = [
  { value: "none", label: "No priority" },
  {
    value: "urgent",
    label: (
      <div className="flex items-center gap-2">
        <div className="text-icon">{getPriorityIcon("urgent")}</div>
        <span>Urgent</span>
      </div>
    ),
  },
  {
    value: "high",
    label: (
      <div className="flex items-center gap-2">
        <div className="text-icon">{getPriorityIcon("high")}</div>
        <span>High</span>
      </div>
    ),
  },
  {
    value: "medium",
    label: (
      <div className="flex items-center gap-2">
        <div className="text-icon">{getPriorityIcon("medium")}</div>
        <span>Medium</span>
      </div>
    ),
  },
  {
    value: "low",
    label: (
      <div className="flex items-center gap-2">
        <div className="text-icon">{getPriorityIcon("low")}</div>
        <span>Low</span>
      </div>
    ),
  },
]

interface TaskCardProps {
  task: Task
  size: "sm" | "md" | "lg"
  onComplete?: (task: Task) => void
  onAccept?: (task: Task) => void
  onReject?: (task: Task) => void
  eventId?: string
  hideHousehold?: boolean
  hideProject?: boolean
  hideAssignee?: boolean
  hideBorder?: boolean
  hidePriority?: boolean
  hideLabel?: boolean
  allowDelete?: boolean
  allowReject?: boolean
  showMeetingLink?: boolean
  handleSeekTo?: (time: number) => void
  taskList?: Task[]
  taskIndex?: number
  openInStack?: boolean
  showBookBadge?: boolean
  selected?: boolean
  onSelectedChange?: (selected: boolean) => void
  selectionDisabled?: boolean
}

const TaskCard = forwardRef<HTMLDivElement, TaskCardProps>(
  (
    {
      task,
      size,
      onComplete,
      onAccept,
      onReject,
      eventId,
      hideHousehold,
      hideProject,
      hideAssignee,
      hideBorder,
      hidePriority,
      hideLabel,
      allowDelete,
      allowReject,
      showMeetingLink,
      handleSeekTo,
      taskList,
      taskIndex,
      openInStack,
      showBookBadge,
      selected,
      onSelectedChange,
      selectionDisabled,
      ...props
    },
    ref
  ) => {
    const { openSlideover, pushSlideover, closeSlideover } = useGlobalSlideoverStore()
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [showStopRecurrenceConfirm, setShowStopRecurrenceConfirm] = useState(false)

    const { localOptimisticStatus, handleComplete, handleDateUpdate } = useOptimisticTaskActions({
      task,
      onComplete,
      eventId,
    })

    const deleteTaskMutation = useDeleteTask()
    const deactivateTaskRecurrenceMutation = useDeactivateTaskRecurrence()
    const { mutate: updateTask } = useUpdateTask()
    const isCompleted = localOptimisticStatus === TaskStatus.DONE
    const isPendingRecommendation = task.was_recommended === true && task.status === TaskStatus.PENDING
    const isPendingOrRejected = task.status === TaskStatus.PENDING || task.status === TaskStatus.REJECTED

    const handlePriorityChange = (value: string) => {
      const priority = value === "none" ? null : (value as TaskPriority)
      updateTask(
        { id: task.id!, priority },
        {
          onSuccess: () => {
            toast.success("Task updated")
          },
        }
      )
    }
    const handleAcceptTask = () => {
      onAccept?.(task)
      updateTask({ id: task.id!, status: TaskStatus.TODO }, { onSuccess: () => toast.success("Task accepted") })
    }

    const handleRejectTask = () => {
      onReject?.(task)
      updateTask({ id: task.id!, status: TaskStatus.REJECTED }, { onSuccess: () => toast.success("Task rejected") })
    }

    const handleUndoReject = () => {
      const restoreStatus = task.was_recommended ? TaskStatus.PENDING : TaskStatus.TODO
      updateTask({ id: task.id!, status: restoreStatus }, { onSuccess: () => toast.success("Task restored") })
    }

    const recurrence = task.task_recurrence
    const recurrenceDeactivated =
      !!recurrence?.deactivated_at ||
      (deactivateTaskRecurrenceMutation.isSuccess &&
        deactivateTaskRecurrenceMutation.variables?.recurrenceId === recurrence?.id)
    const recurrenceActive = !!recurrence && !recurrenceDeactivated
    const intervalValue = recurrence?.interval_value ?? 1
    const intervalUnit = recurrence?.interval_unit ?? "weeks"
    const UNIT_LABELS = {
      days: { singular: "day", plural: "days" },
      weeks: { singular: "week", plural: "weeks" },
      months: { singular: "month", plural: "months" },
      years: { singular: "year", plural: "years" },
    } as const
    const unitMeta = UNIT_LABELS[intervalUnit as keyof typeof UNIT_LABELS] ?? UNIT_LABELS.weeks
    const weekdaySummary = monthlyWeekdaySummary(
      recurrence?.monthly_rule_type,
      recurrence?.weekday_ordinal,
      recurrence?.weekday
    )
    const intervalSummary = recurrence
      ? intervalValue === 1
        ? `Every ${unitMeta.singular}${weekdaySummary ? ` ${weekdaySummary}` : ""}`
        : `Every ${intervalValue} ${unitMeta.plural}${weekdaySummary ? ` ${weekdaySummary}` : ""}`
      : null
    const nextOccurrenceDate = recurrence?.next_run_at ? new Date(recurrence.next_run_at) : null
    const nextOccurrenceRelative = nextOccurrenceDate
      ? formatDistanceToNow(nextOccurrenceDate, { addSuffix: true })
      : null
    const nextOccurrenceExact = nextOccurrenceDate ? format(nextOccurrenceDate, "MMM d, yyyy") : null
    const showRelativeNext = Boolean(
      nextOccurrenceDate && Math.abs(nextOccurrenceDate.getTime() - Date.now()) <= 7 * 24 * 60 * 60 * 1000
    )
    const startDate = recurrence?.start_at ? new Date(recurrence.start_at) : null
    const startExact = startDate ? format(startDate, "MMM d, yyyy") : null
    const startRelative = startDate ? formatDistanceToNow(startDate, { addSuffix: true }) : null
    const showStartRelative = Boolean(
      startDate && Math.abs(startDate.getTime() - Date.now()) <= 7 * 24 * 60 * 60 * 1000
    )
    const recurrenceBadgeText = recurrenceActive
      ? [
          intervalSummary,
          nextOccurrenceDate
            ? showRelativeNext && nextOccurrenceRelative
              ? `next ${nextOccurrenceRelative}`
              : nextOccurrenceExact
                ? `next ${nextOccurrenceExact}`
                : null
            : startDate
              ? showStartRelative && startRelative
                ? `starting ${startRelative}`
                : startExact
                  ? `starting ${startExact}`
                  : null
              : null,
        ]
          .filter(Boolean)
          .join(" • ")
      : null
    const recurrenceStoppedDate = recurrence?.deactivated_at
      ? new Date(recurrence.deactivated_at)
      : recurrenceDeactivated
        ? new Date(deactivateTaskRecurrenceMutation.submittedAt)
        : null
    const recurrenceStoppedExact = recurrenceStoppedDate ? format(recurrenceStoppedDate, "MMM d, yyyy") : null
    const recurrenceInactiveText = [
      intervalSummary,
      recurrenceStoppedExact ? `stopped ${recurrenceStoppedExact}` : "stopped",
    ]
      .filter(Boolean)
      .join(" • ")

    const handleDeleteTask = () => {
      if (!task.id) return
      deleteTaskMutation.mutate(task.id, {
        onSuccess: () => {
          toast.success("Task deleted successfully")
          setShowDeleteConfirm(false)
        },
      })
    }

    const openTaskDetail = () => {
      const slideoverAction = openInStack ? pushSlideover : openSlideover
      slideoverAction({
        content:
          taskList && taskIndex !== undefined ? (
            <TaskDetailWrapper
              initialTaskId={task.id || ""}
              taskList={taskList}
              initialIndex={taskIndex}
              onComplete={(taskId, completed) => {
                if (taskId) handleComplete(completed)
              }}
            />
          ) : (
            <TaskDetailSlideover
              taskId={task.id || null}
              onComplete={(taskId, completed) => {
                if (taskId) handleComplete(completed)
              }}
            />
          ),
      })
    }

    const handleCardClick = (e: React.MouseEvent) => {
      if (
        !(e.target as HTMLElement).closest(".task-checkbox") &&
        !(e.target as HTMLElement).closest(".priority-selector") &&
        !(e.target as HTMLElement).closest(".date-selector") &&
        !(e.target as HTMLElement).closest(".assignee-selector") &&
        !(e.target as HTMLElement).closest(".action-buttons") &&
        !(e.target as HTMLElement).closest(".household-selector") &&
        !(e.target as HTMLElement).closest(".project-selector") &&
        !(e.target as HTMLElement).closest(".recommendable-selector")
      ) {
        openTaskDetail()
      }
    }

    const handleDeactivateRecurrence = () => {
      if (!recurrence?.id) return
      deactivateTaskRecurrenceMutation.mutate(
        { recurrenceId: recurrence.id },
        {
          onSuccess: () => {
            toast.success("Repeating stopped")
            setShowStopRecurrenceConfirm(false)
          },
        }
      )
    }

    const hasTranscriptTimestamp = Boolean(task.transcript_timestamp)
    const canJumpInPlace = Boolean(handleSeekTo)
    const canJumpToMeeting = Boolean(
      hasTranscriptTimestamp && !canJumpInPlace && task.recommendable_type === "Meeting" && task.recommendable_id
    )
    const showTranscriptJump = hasTranscriptTimestamp && (canJumpInPlace || canJumpToMeeting)
    const transcriptJumpMs = Math.max(0, ((task.transcript_timestamp ?? 0) - TRANSCRIPT_SEEK_OFFSET_SECONDS) * 1000)
    const meetingJumpHref =
      canJumpToMeeting && task.recommendable_id ? `/meetings/${task.recommendable_id}?t=${transcriptJumpMs}` : null
    const showDeleteMenu = Boolean(allowDelete)
    const showActionButtons = showTranscriptJump || showDeleteMenu

    const actionButtonContent = (
      <>
        {showTranscriptJump && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {canJumpInPlace ? (
                  <Button
                    variant="ghost"
                    size="iconXs"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleSeekTo?.(transcriptJumpMs)
                    }}
                  >
                    <Sparkles className="text-icon size-3.5!" />
                  </Button>
                ) : (
                  <Link
                    href={meetingJumpHref!}
                    onClick={(e) => {
                      e.stopPropagation()
                      closeSlideover()
                    }}
                    className="hover:bg-accent inline-flex size-6 items-center justify-center rounded-md"
                  >
                    <Sparkles className="text-icon size-3.5!" />
                  </Link>
                )}
              </TooltipTrigger>
              <TooltipContent>Jump to video</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {showDeleteMenu && (
          <Button
            variant="ghost"
            size="iconXs"
            onClick={(e) => {
              e.stopPropagation()
              setShowDeleteConfirm(true)
            }}
          >
            <Trash2 className="text-icon size-3.5!" />
          </Button>
        )}
      </>
    )

    return (
      <>
        <div
          ref={ref}
          className={cn(
            "hover:bg-secondary relative flex w-full cursor-pointer gap-3 p-4 transition-colors",
            size === "sm" ? "items-center" : "items-start",
            !hideBorder && "border-b"
          )}
          onClick={handleCardClick}
          {...props}
        >
          {(isCompleted || task.status === TaskStatus.REJECTED) && (
            <div className="bg-background animate-in fade-in pointer-events-none absolute inset-0 z-10 opacity-50 duration-500"></div>
          )}

          {onSelectedChange && (
            <div className="flex h-7 items-center" onClick={(event) => event.stopPropagation()}>
              <Checkbox
                checked={selected}
                disabled={selectionDisabled}
                onCheckedChange={(checked) => onSelectedChange(checked === true)}
                aria-label="Select task"
              />
            </div>
          )}

          {!isPendingOrRejected && (
            <div className="task-checkbox" onClick={(e) => e.stopPropagation()}>
              <div className={size === "sm" ? "flex h-7 w-7 items-center justify-center" : ""}>
                <CircleCheckbox
                  checked={isCompleted}
                  onChange={handleComplete}
                  className={size === "lg" ? "mt-1 size-[18px]" : size === "md" ? "size-[18px]" : ""}
                />
              </div>
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex items-center gap-1">
              <LexicalPreview
                content={task.title}
                className={cn(
                  "line-clamp-2",
                  size === "sm"
                    ? "text-sm leading-tight font-medium"
                    : size === "md"
                      ? "text-sm leading-tight"
                      : "text-base",
                  isCompleted && "text-muted-foreground line-through"
                )}
              />
            </div>
            <div
              className={cn(
                "flex w-full items-center gap-x-2.5 gap-y-1",
                size === "sm" ? "flex-nowrap overflow-hidden" : "flex-wrap",
                isCompleted && "pointer-events-none"
              )}
            >
              {!hidePriority && (
                <div className="priority-selector flex-none" onClick={(e) => e.stopPropagation()}>
                  <SelectInput
                    id={`task-priority-${task.id}`}
                    value={task.priority ?? "none"}
                    onChange={handlePriorityChange}
                    options={PRIORITY_OPTIONS}
                    contentProps={{ align: "start", className: "w-40!" }}
                    disabled={isCompleted}
                    triggerButton={
                      <button
                        type="button"
                        disabled={isCompleted}
                        className="text-icon hover:bg-secondary hover:text-foreground flex size-5.5 flex-none items-center justify-center rounded-sm transition-colors disabled:pointer-events-none disabled:opacity-50"
                      >
                        {getPriorityIcon(task.priority)}
                      </button>
                    }
                  />
                </div>
              )}
              {!hideLabel && task.tags?.[0] && (
                <Badge variant="outline" className="min-w-0 overflow-hidden" size="base">
                  <div
                    className="mr-1 size-1.5 flex-none rounded-full"
                    style={{ backgroundColor: task.tags[0].color || "var(--color-secondary)" }}
                  />
                  <span className="truncate">{task.tags[0].name}</span>
                </Badge>
              )}
              {task.is_private && (
                <HoverCard openDelay={150} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      className="text-muted-foreground hover:text-foreground flex flex-none items-center"
                      aria-label="View who can see this private task"
                    >
                      <Lock className="size-3.5" />
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent side="top" align="start" className="w-64 p-3">
                    <PrivacyViewerList
                      creatorId={task.created_by_id ?? task.created_by?.id ?? null}
                      assigneeId={task.assigned_to_id ?? task.assigned_to?.id ?? null}
                      teamId={task.assigned_team_id ?? task.assigned_team?.id ?? null}
                      collaboratorIds={task.collaborator_ids ?? task.collaborators?.map((c) => c.id) ?? []}
                    />
                  </HoverCardContent>
                </HoverCard>
              )}
              {task.was_recommended && task.status !== TaskStatus.PENDING && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-icon flex flex-none items-center" aria-label="Generated by AI">
                        <WandSparkles className="size-3.5" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">Generated by AI</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {task.deadline_at && <Deadline task={task} onDateUpdate={handleDateUpdate} isCompleted={isCompleted} />}
              {showBookBadge && task.book_initials && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <BookIcon
                        bookId={task.book_id}
                        initials={task.book_initials}
                        name={task.book_name}
                        className="bg-secondary text-muted-foreground size-5 rounded-sm"
                        textClassName="text-[10px] font-medium"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top">{task.book_name}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {!!task.comment_count && task.comment_count > 0 && (
                <span className="text-muted-foreground flex items-center gap-0.5 text-sm/none">
                  <MessageSquare className="size-3 flex-none" />
                  {task.comment_count}
                </span>
              )}
              {task.taskable && !hideHousehold && (
                <div className="household-selector" onClick={(e) => e.stopPropagation()}>
                  {isTaskableHousehold(task.taskable, task.taskable_type) ? (
                    <Link
                      key={task.taskable.id}
                      href={`/${householdRouteSegment(task.taskable.type)}/${task.taskable.id}`}
                      className={cn(
                        "text-muted-foreground hover:border-foreground hover:text-foreground flex items-center gap-0.5 border-b border-dashed border-transparent text-sm/none",
                        size === "sm" && "max-w-36 truncate"
                      )}
                    >
                      <AtSign className="size-3 flex-none" />
                      <span className="truncate">{task.taskable.name}</span>
                    </Link>
                  ) : isTaskableContact(task.taskable, task.taskable_type) ? (
                    <Link
                      key={task.taskable.id}
                      href={`/contacts/${task.taskable.id}`}
                      className={cn(
                        "text-muted-foreground hover:border-foreground hover:text-foreground flex items-center gap-0.5 border-b border-dashed border-transparent text-sm/none",
                        size === "sm" && "max-w-36 truncate"
                      )}
                    >
                      <AtSign className="size-3 flex-none" />
                      <span className="truncate">{task.taskable.person?.full_name || "Contact"}</span>
                    </Link>
                  ) : isTaskableBusiness(task.taskable, task.taskable_type) ? (
                    <Link
                      key={task.taskable.id}
                      href={`/businesses/${task.taskable.id}`}
                      className={cn(
                        "text-muted-foreground hover:border-foreground hover:text-foreground flex items-center gap-0.5 border-b border-dashed border-transparent text-sm/none",
                        size === "sm" && "max-w-36 truncate"
                      )}
                    >
                      <Building2 className="size-3 flex-none" />
                      <span className="truncate">{task.taskable.name || "Business"}</span>
                    </Link>
                  ) : null}
                </div>
              )}
              {task.household && !hideHousehold && !task.taskable && (
                <div className="household-selector" onClick={(e) => e.stopPropagation()}>
                  <Link
                    key={task.household.id}
                    href={`/${householdRouteSegment(task.household.type)}/${task.household.id}`}
                    className={cn(
                      "text-muted-foreground hover:border-foreground hover:text-foreground flex items-center gap-0.5 border-b border-dashed border-transparent text-sm/none",
                      size === "sm" && "max-w-36 truncate"
                    )}
                  >
                    <AtSign className="size-3 flex-none" />
                    <span className="truncate">{task.household.name}</span>
                  </Link>
                </div>
              )}
              {task.project && !hideProject && (
                <div className="project-selector" onClick={(e) => e.stopPropagation()}>
                  <Link
                    key={task.project.id}
                    href={`/projects/${task.project.id}`}
                    className={cn(
                      "text-muted-foreground hover:border-foreground hover:text-foreground flex items-center gap-1 border-b border-dashed border-transparent text-sm/none",
                      size === "sm" && "max-w-36 truncate"
                    )}
                  >
                    <FolderOpen className="size-3 flex-none" />
                    <span className="truncate">{task.project.name}</span>
                  </Link>
                </div>
              )}
              {showMeetingLink && task.recommendable_type === "Meeting" && task.recommendable_id && (
                <div className="recommendable-selector" onClick={(e) => e.stopPropagation()}>
                  <Link
                    href={`/meetings/${task.recommendable_id}`}
                    onClick={closeSlideover}
                    className={cn(
                      "text-muted-foreground hover:border-foreground hover:text-foreground flex items-center gap-1 border-b border-dashed border-transparent text-sm/none",
                      size === "sm" && "max-w-36 truncate"
                    )}
                  >
                    <CalendarDays className="size-3 flex-none" />
                    <span className="truncate">{task.recommendable?.title || "Meeting"}</span>
                  </Link>
                </div>
              )}
              {size !== "sm" && recurrence && recurrenceActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  className={cn(
                    "repeat-badge text-muted-foreground hover:text-destructive hover:bg-destructive/10 border-border pointer-events-auto flex items-center gap-2 rounded-full border border-dashed px-2 py-1 text-xs focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
                    isCompleted && "opacity-75"
                  )}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setShowStopRecurrenceConfirm(true)
                  }}
                  disabled={deactivateTaskRecurrenceMutation.isPending}
                >
                  <Repeat className="size-3" />
                  <span className="hidden sm:inline">{recurrenceBadgeText || "Repeats"}</span>
                  <Trash2 className="size-3" />
                </Button>
              )}
              {recurrence && !recurrenceActive && (
                <div className="repeat-badge text-muted-foreground border-border flex items-center gap-2 rounded-full border border-dashed px-2 py-1 text-xs">
                  <Repeat className="size-3" />
                  <span className="hidden sm:inline">{recurrenceInactiveText || "Repeats • stopped"}</span>
                </div>
              )}
              {size === "sm" && !hideAssignee && (
                <div className="assignee-selector" onClick={(e) => e.stopPropagation()}>
                  <AssignedToCell task={task} />
                </div>
              )}
            </div>
            {isPendingRecommendation && (
              <div className="mt-1.5 -mb-1 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <Button variant="outlineSuccess" size="xs" disabled={selectionDisabled} onClick={handleAcceptTask}>
                  <ThumbsUp />
                  Accept
                </Button>
                <Button variant="outlineDestructive" size="xs" disabled={selectionDisabled} onClick={handleRejectTask}>
                  <ThumbsDown />
                  Reject
                </Button>
              </div>
            )}
            {task.status === TaskStatus.REJECTED && (
              <div className="mt-1.5 -mb-1 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <Badge variant="destructive">Rejected</Badge>
                <Button variant="outline" size="iconXs" onClick={handleUndoReject}>
                  <Undo2 className="text-icon size-3.5!" />
                </Button>
              </div>
            )}
          </div>

          {size === "sm" && (
            <div
              className="action-buttons absolute right-2 bottom-2 flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {allowReject && task.status === TaskStatus.TODO && (
                <Button
                  variant="ghost"
                  size="iconXs"
                  onClick={handleRejectTask}
                  tooltip="Reject task"
                  className="text-icon hover:text-red-accent hover:bg-red-background"
                >
                  <ThumbsDown className="size-3.5!" />
                </Button>
              )}
              {showActionButtons && actionButtonContent}
            </div>
          )}

          {(size === "lg" || size === "md") && (
            <div className="flex items-center gap-3 self-center">
              {!hideAssignee && (
                <div className="flex flex-col items-end gap-1 text-right">
                  <p className="text-muted-foreground text-xs leading-none">Assigned to</p>
                  <div className="assignee-selector" onClick={(e) => e.stopPropagation()}>
                    <AssignedToCell task={task} />
                  </div>
                </div>
              )}
              {allowReject && task.status === TaskStatus.TODO && (
                <Button
                  variant="ghost"
                  size="iconXs"
                  onClick={handleRejectTask}
                  tooltip="Reject task"
                  className="text-icon hover:text-red-accent hover:bg-red-background"
                >
                  <ThumbsDown className="size-3.5!" />
                </Button>
              )}
              {showActionButtons && (
                <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                  {actionButtonContent}
                </div>
              )}
            </div>
          )}
        </div>
        {showStopRecurrenceConfirm && (
          <div onClick={(e) => e.stopPropagation()}>
            <ConfirmDialog
              open={showStopRecurrenceConfirm}
              onOpenChange={setShowStopRecurrenceConfirm}
              title={"Stop repeating?"}
              description={"This will stop new occurrences from being scheduled."}
              dismiss={"explicit"}
              cancel={{ onClick: () => setShowStopRecurrenceConfirm(false) }}
              action={{ label: "Stop repeating", onClick: handleDeactivateRecurrence }}
            />
          </div>
        )}

        <ConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title={"Delete task?"}
          description={"This action cannot be undone. This will permanently delete the task."}
          dismiss={"explicit"}
          cancel={{ onClick: () => setShowDeleteConfirm(false) }}
          action={{ label: "Delete", onClick: handleDeleteTask }}
        />
      </>
    )
  }
)

TaskCard.displayName = "TaskCard"

export { TaskCard }
export type { TaskCardProps }
